import {
    IconFileCode, IconFileMusic, IconFileText, IconFileTypeDocx, IconFileTypePdf,
    IconFileTypePpt, IconFileTypeXls, IconFileZip, IconFolder, IconPhoto, IconVideo, Icon,
} from '@tabler/icons-react';
import { IOptionStringOrNumberValue } from 'bricks/types';
import {
    DealDirectoryItem, IDealDirectory, IDealDirectoryTree, IDealDocument,
    IFlatDirectory,
} from './types';
import { ASSET_TAGS } from './constants';

interface GetFileIconParams {
    fileFormat?: string;
    isExpanded: boolean;
}

export const getFileIcon = ({ fileFormat, isExpanded }: GetFileIconParams): Icon => {
    const isFolder = !fileFormat;

    if (isFolder) {
        return isExpanded ? IconFolder : IconFolder;
    }

    const format = fileFormat?.toLowerCase();
    switch (format) {
        case 'pdf':
            return IconFileTypePdf;
        case 'doc':
        case 'docx':
            return IconFileTypeDocx;
        case 'xls':
        case 'xlsx':
        case 'xlsm':
        case 'csv':
            return IconFileTypeXls;
        case 'ppt':
        case 'pptx':
            return IconFileTypePpt;
        case 'png':
        case 'jpg':
        case 'jpeg':
        case 'gif':
        case 'bmp':
        case 'svg':
            return IconPhoto;
        case 'mp4':
        case 'mov':
        case 'avi':
        case 'wmv':
            return IconVideo;
        case 'mp3':
        case 'wav':
        case 'ogg':
            return IconFileMusic;
        case 'js':
        case 'ts':
        case 'html':
        case 'css':
        case 'json':
            return IconFileCode;
        case 'zip':
        case 'rar':
        case '7z':
            return IconFileZip;
        default:
            return IconFileText; // Generic file icon for unknown types
    }
};

const mimeToExtension: Record<string, string> = {
    'application/pdf': 'pdf',
    'application/msword': 'doc',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document': 'docx',
    'application/vnd.ms-excel': 'xls',
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': 'xlsx',
    'application/vnd.ms-excel.sheet.macroenabled.12': 'xlsm',
    'text/csv': 'csv',
    'application/vnd.ms-powerpoint': 'ppt',
    'application/vnd.openxmlformats-officedocument.presentationml.presentation': 'pptx',
    'image/png': 'png',
    'image/jpeg': 'jpg',
    'image/gif': 'gif',
    'image/bmp': 'bmp',
    'image/svg+xml': 'svg',
    'video/mp4': 'mp4',
    'video/quicktime': 'mov',
    'video/x-msvideo': 'avi',
    'audio/mpeg': 'mp3',
    'audio/wav': 'wav',
    'application/json': 'json',
    'text/html': 'html',
    'text/css': 'css',
    'application/javascript': 'js',
    'application/zip': 'zip',
    'application/x-rar-compressed': 'rar',
    'application/x-7z-compressed': '7z',
};

export const normalizeFileFormat = (fileFormat?: string, fileName?: string): string | undefined => {
    if (!fileFormat) return undefined;
    const fromMime = mimeToExtension[fileFormat.toLowerCase()];
    if (fromMime) return fromMime;
    if (fileName) {
        const ext = fileName.split('.').pop()?.toLowerCase();
        if (ext && ext !== fileName.toLowerCase()) return ext;
    }
    return fileFormat.split('/').pop();
};

const DARK_BLUE = 'dark_blue';
const NS_EXCEL_GREEN = 'ns_excel_green';
const BLUE = 'blue';

export const iconColorByFilenameExtension: { [key: string]: string } = {
    pdf: BLUE,
    doc: DARK_BLUE,
    docx: DARK_BLUE,
    pptx: BLUE,
    ppt: BLUE,
    xls: NS_EXCEL_GREEN,
    xlsx: NS_EXCEL_GREEN,
    xlsm: NS_EXCEL_GREEN,
    csv: NS_EXCEL_GREEN,
    tiff: BLUE,
    jpg: BLUE,
    jpeg: BLUE,
    bmp: BLUE,
    gif: BLUE,
    png: BLUE,
    mp4: BLUE,
    mov: BLUE,
};

export const extractDirectories = (tree: IDealDirectory, includeRoot: boolean = false, rootValue: string | number = 0) => {
    const directories: IOptionStringOrNumberValue[] = [];

    if (includeRoot) {
        directories.push({ label: 'Home', value: rootValue });
    }

    tree?.children?.forEach((child: IDealDirectory) => {
        if (child?.children?.length) {
            const subDirectories = extractDirectories(child);
            directories.push(...subDirectories);
        }
        directories.push({ label: child.directoryName, value: child.directoryId });
    });

    return directories;
};

export const checkIfIsFile = (item: DealDirectoryItem): item is IDealDocument => typeof item === 'object' && item !== null && 'extension' in item;
export const checkIfIsDirectory = (item: DealDirectoryItem): item is IDealDirectory => 'children' in item;

export const sortDocuments = (
    documents: IDealDocument[],
    sortConfig: { column: string; direction: 'asc' | 'desc' } | null,
): IDealDocument[] => documents.sort((a, b) => {
    if (!sortConfig) {
        return a.name.localeCompare(b.name);
    }

    const { column, direction } = sortConfig;
    const modifier = direction === 'asc' ? 1 : -1;

    switch (column.toLowerCase()) {
        case 'name':
            return modifier * a.name.localeCompare(b.name);
        case 'type':
            return modifier * (a.type || '').localeCompare(b.type || '');
        case 'format':
            return modifier * (a.extension || '').localeCompare(b.extension || '');
        case 'date modified':
            return modifier * (new Date(a.updatedAt || 0).getTime() - new Date(b.updatedAt || 0).getTime());
        case 'size':
            // Simple string comparison for now - could be enhanced to handle actual file sizes
            return modifier * ((a.size || 0) - (b.size || 0));
        default:
            return 0;
    }
});

export const sortItems = (
    items: IDealDirectory[],
    sortConfig: { column: string; direction: 'asc' | 'desc' } | null,
): IDealDirectory[] => items
    .map(item => {
        if (item.children) {
            return {
                ...item,
                children: sortItems(item.children, sortConfig),
                documents: sortDocuments(item.documents, sortConfig),
            };
        }
        return item;
    })
    .sort((a, b) => (a.directoryName || '').localeCompare(b.directoryName || ''));

export function getAssetTagById(id: number): ASSET_TAGS | undefined {
    const assetTagMap: { [key: number]: ASSET_TAGS } = {
        1: ASSET_TAGS.FINANCIAL_PROFORMA,
        2: ASSET_TAGS.LOAN_FINANCING_AGREEMENT,
        3: ASSET_TAGS.JV_AGREEMENT,
        4: ASSET_TAGS.PURCHASE_AGREEMENT,
        5: ASSET_TAGS.ENV_PHASE_1,
        6: ASSET_TAGS.ENV_PHASE_2,
        7: ASSET_TAGS.PERMITS_ENTITLEMENTS,
        8: ASSET_TAGS.DRAWINGS_SCHEMATIC_DESIGN,
        9: ASSET_TAGS.DRAWINGS_DESIGN_DEV,
        10: ASSET_TAGS.DRAWINGS_FILING_SET,
        11: ASSET_TAGS.DRAWINGS_CONSTRUCTION_DOCS,
        12: ASSET_TAGS.DRAWINGS_AMENDMENTS_BULLETINS,
        13: ASSET_TAGS.LEGAL,
        14: ASSET_TAGS.LEASE_AGREEMENT,
        15: ASSET_TAGS.CAD_BIM,
        16: ASSET_TAGS.RENDERINGS,
        17: ASSET_TAGS.GEOTECHNICAL_REPORT,
        18: ASSET_TAGS.FIELD_DAILY_REPORT,
        19: ASSET_TAGS.RFI,
        20: ASSET_TAGS.FFE,
        21: ASSET_TAGS.SIGNAGE_WAYFINDING,
        22: ASSET_TAGS.PUNCH_LIST,
        23: ASSET_TAGS.OPS_MAINTENANCE_MANUAL,
        24: ASSET_TAGS.WARRANTIES,
        25: ASSET_TAGS.MUNICIPAL_GOV_DOCS,
        26: ASSET_TAGS.PROGRESS_PHOTOS,
        27: ASSET_TAGS.OTHER,
        28: ASSET_TAGS.SUBMITTAL,
        29: ASSET_TAGS.UTILITY_AGREEMENT,
    };

    return assetTagMap[id];
}

export const buildDirectoryTree = (files: { relativePath?: string }[]): IDealDirectoryTree[] => {
    const root: Record<string, any> = {};

    files.forEach(file => {
        const pathParts = file.relativePath?.split('/')?.filter(Boolean) ?? [];
        let current = root;

        pathParts.slice(0, -1).forEach(folder => {
            if (!current[folder]) {
                current[folder] = {};
            }
            current = current[folder];
        });
    });

    const convertToTree = (node: Record<string, any>): IDealDirectoryTree[] => Object.entries(node).map(([name, children]) => ({
        name,
        children: convertToTree(children),
    }));

    return convertToTree(root);
};

export const collectFullPaths = (
    directories: IFlatDirectory[],
): Record<string, number> => {
    const idToDirectory = new Map<number, IFlatDirectory>();
    const idToPath = new Map<number, string>();

    directories.forEach(dir => {
        idToDirectory.set(dir.id, dir);
    });

    const resolvePath = (dir: IFlatDirectory): string => {
        if (idToPath.has(dir.id)) return idToPath.get(dir.id)!;

        if (!dir.parentDirectoryId || !idToDirectory.has(dir.parentDirectoryId)) {
            idToPath.set(dir.id, dir.name);
            return dir.name;
        }

        const parent = idToDirectory.get(dir.parentDirectoryId)!;
        const fullPath = `${resolvePath(parent)}/${dir.name}`;
        idToPath.set(dir.id, fullPath);
        return fullPath;
    };

    const result: Record<string, number> = {};
    directories.forEach(dir => {
        const fullPath = resolvePath(dir);
        result[fullPath] = dir.id;
    });

    return result;
};
